import { useTranslation } from "react-i18next";
import { useTitle } from "../../context/TitleContext";
import PermissionMotionButton from "../PermissionMotionButton";
import PermissionButton from "../PermissionButton";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteCategory,
  fetchCategories,
  setSelectedCategoryForEdit,
  clearCategoryState,
} from "../../redux/Slices/CategorySlice";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FaLayerGroup, FaPlus, FaEdit, FaTrash } from "react-icons/fa";
import Pagination from '../../components/Pagination';

function Category() {
  const { t, i18n } = useTranslation("global");
  const { setTitle } = useTitle();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const { data, meta, loading } = useSelector(
    (state) => state.categories
  );

  useEffect(() => {
    setTitle(t("sidenav.category"));
    dispatch(clearCategoryState());
  }, [setTitle, t, i18n.language, dispatch]);

  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deletingCategory, setDeletingCategory] = useState(null);

  useEffect(() => {
    dispatch(fetchCategories(currentPage));
  }, [dispatch, currentPage]);

  const handleDeleteClick = (category) => {
    setDeleteId(category.id);
    setDeletingCategory(category);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (deleteId) {
      dispatch(deleteCategory(deleteId))
        .unwrap()
        .then((result) => {
          toast.success(result?.message || t("labels.categoryDeleted"));
          setShowDeleteModal(false);
          setDeleteId(null);
          setDeletingCategory(null);
          dispatch(fetchCategories(currentPage));
        })
        .catch((err) => toast.error(err));
    }
  };

  const handleEditClick = (category) => {
    dispatch(setSelectedCategoryForEdit(category));
  };

  const handlePageChange = (page) => {
    if (loading) return;
    if (page === currentPage) return;
    setCurrentPage(page);
  };



  const overlay = { position: "fixed", inset: 0, background: "rgba(15,23,42,.55)", zIndex: 1040, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 };

  if (loading && (!data || data.length === 0)) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
        <div className="spinner-border text-danger" role="status">
          <span className="visually-hidden">جاري التحميل...</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "4px 0", direction: "rtl" }}>
      <style>{`
        .category-table {
          width: 100%;
          border-collapse: collapse;
          text-align: right;
        }
        .category-table th {
          background-color: #f8fafc !important;
          color: #475569 !important;
          font-weight: 700 !important;
          font-size: 13px !important;
          padding: 14px 16px !important;
          border-bottom: 1px solid #e2e8f0 !important;
          text-align: center !important;
        }
        .category-table td {
          padding: 14px 16px !important;
          font-size: 14px !important;
          vertical-align: middle !important;
          border-bottom: 1px solid #f1f5f9 !important;
          text-align: center !important;
        }
      `}</style>

      {/* ── Header ─────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <div>
          <h4 style={{ margin: 0, fontWeight: 800, color: "#0f172a", fontSize: 20, display: "flex", alignItems: "center", gap: 8 }}>
            📂 تصنيفات المنتجات (Categories)
          </h4>
          <p style={{ margin: "2px 0 0", color: "#64748b", fontSize: 13 }}>تعديل ومراقبة وإضافة فئات الأكواد الرقمية والكتب والملخصات المطبوعة</p>
        </div>
        <PermissionMotionButton
          permission="create-categories"
          onClick={() => navigate("/add_category")}
          style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 20px", borderRadius: 10, border: "none",
            background: "linear-gradient(135deg,var(--main-color),var(--main-light))", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 14 }}
        >
          <FaPlus size={12} /> إضافة فئة جديدة
        </PermissionMotionButton>
      </div>

      {/* ── Table Container ────────────────────────────── */}
      <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,.04)", padding: 0 }}>
        {(!data || data.length === 0) ? (
          <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>
            <FaLayerGroup size={48} className="text-muted mb-3" />
            <p style={{ marginTop: 12, fontWeight: 600, fontSize: 15, color: "#64748b" }}>لا توجد فئات حالياً</p>
          </div>
        ) : (
          <div style={{ padding: 0 }}>
            <div className="table-responsive">
              <table className="category-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>الاسم العربي</th>
                    <th>الاسم الإنجليزي</th>
                    <th>الحالة</th>
                    <th>تاريخ الإنشاء</th>
                    <th>الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((cat, index) => (
                    <tr key={cat.id}>
                      <td>
                        {meta ? (meta.current_page - 1) * meta.per_page + index + 1 : index + 1}
                      </td>
                      <td style={{ fontWeight: 600, color: "#0f172a" }}>{cat.name_ar}</td>
                      <td style={{ color: "#475569" }}>{cat.name_en}</td>
                      <td>
                        <span style={{ fontSize: 11, fontWeight: 700, color: cat.status === "active" ? "#15803d" : "#64748b", background: cat.status === "active" ? "#d1fae5" : "#f1f5f9", padding: "3px 10px", borderRadius: 20 }}>
                          {cat.status === "active" ? "نشط" : "غير نشط"}
                        </span>
                      </td>
                      <td style={{ fontSize: 13, color: "#64748b" }}>
                        {new Date(cat.created_at).toLocaleDateString("ar-EG", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })}
                      </td>
                      <td>
                        <div style={{ display: "inline-flex", gap: 5, justifyContent: "center" }}>
                          <PermissionButton
                            onClick={() => {
                              handleEditClick(cat);
                              navigate(`/edit_category/${cat.id}`);
                            }}
                            permission="edit-categories"
                            title="تعديل"
                            style={{ width: 30, height: 30, borderRadius: 8, border: "1px solid var(--warning-color)", background: "#fefce8", color: "var(--warning-color)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                          >
                            <FaEdit size={14} />
                          </PermissionButton>
                          <PermissionButton
                            onClick={() => handleDeleteClick(cat)}
                            permission="delete-categories"
                            title="حذف"
                            style={{ width: 30, height: 30, borderRadius: 8, border: "1px solid #fecaca", background: "#fef2f2", color: "var(--red-color)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                          >
                            <FaTrash size={14} />
                          </PermissionButton>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ padding: "0 16px 20px" }}>
              <Pagination meta={meta} currentPage={currentPage} loading={loading} onChange={handlePageChange} />
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div style={overlay}>
          <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 480, boxShadow: "0 25px 60px rgba(0,0,0,.25)", overflow: "hidden" }}>
            <div style={{ padding: "18px 24px", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontWeight: 700, fontSize: 17, display: "flex", alignItems: "center", gap: 6 }}><FaTrash /> تأكيد الحذف</span>
              <button onClick={() => { setShowDeleteModal(false); setDeleteId(null); setDeletingCategory(null); }} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#94a3b8" }}>✕</button>
            </div>
            <div style={{ padding: "18px 24px" }}>
              <p style={{ margin: 0, color: "#334155" }}>هل أنت متأكد من حذف هذا التصنيف بالكامل؟</p>
              <div style={{ marginTop: 10, padding: "10px 14px", background: "#f8fafc", borderRadius: 8, fontSize: 13, color: "#64748b", borderRight: "3px solid var(--red-color)" }}>
                📂 {deletingCategory?.name_ar} ({deletingCategory?.name_en})
              </div>
              <p style={{ margin: "10px 0 0", color: "var(--red-color)", fontSize: 13 }}>⚠ هذا الإجراء لا يمكن التراجع عنه وسيلغي تقسيم كافة المنتجات التابعة له.</p>
            </div>
            <div style={{ padding: "14px 24px", background: "#f8fafc", display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={() => { setShowDeleteModal(false); setDeleteId(null); setDeletingCategory(null); }} style={{ padding: "8px 20px", borderRadius: 9, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontWeight: 600, cursor: "pointer" }}>إلغاء</button>
              <button onClick={handleConfirmDelete} style={{ padding: "8px 20px", borderRadius: 9, border: "none", background: "var(--red-color)", color: "#fff", fontWeight: 600, cursor: "pointer" }}>حذف التصنيف</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Category;
